import React from 'react';
import { Modal, Form, Input, Select, ColorPicker, Switch, Button, InputNumber, Alert } from 'antd';
import { Plus, CircleMinus, Tags, TriangleAlert, Lightbulb, ChartColumn } from 'lucide-react';
import { categoryList } from '../../common';
import { useTagFormController } from './tagForm.controller';

const { Option } = Select;
const { TextArea } = Input;

// Model type labels for display
const modelLabels = {
    User: 'Usuarios',
    Client: 'Clientes',
    Store: 'Tiendas',
    Product: 'Productos',
    TradeCampaign: 'Campañas',
    TradeContract: 'Contrataciones'
};

function TagFormModal({ visible, tag, onSuccess, onCancel, onMessage }) {
    const {
        form,
        loading,
        hasSosTargets,
        setHasSosTargets,
        colorValue,
        changeColor,
        removedModels,
        handleSubmit,
        handleApplicableModelsChange
    } = useTagFormController({ visible, tag, onSuccess, onMessage });

    return (
        <Modal
            title={
                <div className="flex items-center gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-[16px] text-primary-600">
                        <Tags />
                    </span>
                    <div className="min-w-0">
                        <div className="text-[15px] font-semibold text-surface-900">{tag ? 'Editar Etiqueta' : 'Nueva Etiqueta'}</div>
                        <div className="text-xs font-normal text-surface-500">
                            {tag ? 'Modifica los datos de la etiqueta' : 'Crea una nueva etiqueta personalizada'}
                        </div>
                    </div>
                </div>
            }
            open={visible}
            onCancel={onCancel}
            footer={null}
            width={700}
            destroyOnClose>
            <Form form={form} layout="vertical" onFinish={handleSubmit} className="mt-5">
                <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-x-4">
                    <Form.Item name="name" label="Nombre de la Etiqueta" rules={[{ required: true, message: 'Nombre requerido' }]}>
                        <Input placeholder="Ej: Oriente, Gold, Silver" />
                    </Form.Item>

                    <Form.Item label="Color">
                        <ColorPicker value={colorValue} onChange={changeColor} showText />
                    </Form.Item>
                </div>

                <Form.Item name="description" label="Descripción">
                    <TextArea rows={2} placeholder="Descripción opcional de la etiqueta" />
                </Form.Item>

                <Form.Item name="applicableModels" label="Aplicable a" rules={[{ required: true, message: 'Selecciona al menos un modelo' }]}>
                    <Select mode="multiple" placeholder="Selecciona los tipos de elementos" onChange={handleApplicableModelsChange}>
                        <Option value="User">Usuarios</Option>
                        <Option value="Client">Clientes</Option>
                        <Option value="Store">Tiendas</Option>
                        <Option value="Product">Productos</Option>
                        <Option value="TradeCampaign">Campañas</Option>
                        <Option value="TradeContract">Contrataciones</Option>
                    </Select>
                </Form.Item>

                {/* Warning when removing applicable models from an existing tag */}
                {tag && removedModels.length > 0 && (
                    <Alert
                        type="warning"
                        showIcon
                        icon={<TriangleAlert />}
                        className="!mb-4"
                        message="Se eliminarán etiquetas asignadas"
                        description={
                            <span className="text-sm">
                                Al quitar <strong>{removedModels.map((m) => modelLabels[m] || m).join(', ')}</strong> de "Aplicable a", todos los elementos de
                                ese tipo que tengan esta etiqueta serán desetiquetados automáticamente.
                            </span>
                        }
                    />
                )}

                <Form.Item noStyle shouldUpdate={(prev, curr) => prev.applicableModels !== curr.applicableModels}>
                    {({ getFieldValue }) => {
                        const models = getFieldValue('applicableModels') || [];
                        const storeIsApplicable = models.includes('Store');

                        return (
                            <div
                                className={`mb-4 rounded-xl border p-4 ${
                                    storeIsApplicable ? 'border-surface-200 bg-surface-50' : 'border-warning-border bg-warning-bg'
                                }`}>
                                <div className="flex items-center justify-between gap-3">
                                    <span className="flex items-center gap-2 text-sm font-medium text-surface-900">
                                        <ChartColumn className="text-surface-500" />
                                        Objetivos de Share of Shelf (SOS)
                                    </span>
                                    <Switch
                                        checked={hasSosTargets && storeIsApplicable}
                                        onChange={setHasSosTargets}
                                        checkedChildren="Habilitado"
                                        unCheckedChildren="Deshabilitado"
                                        disabled={!storeIsApplicable}
                                    />
                                </div>
                                {storeIsApplicable ? (
                                    <p className="m-0 mt-1 text-xs text-surface-500">Define el porcentaje objetivo de Share of Shelf por categoría</p>
                                ) : (
                                    <p className="m-0 mt-1.5 flex items-start gap-1.5 text-xs text-warning-fg">
                                        <TriangleAlert className="mt-px shrink-0" />
                                        <span>Los objetivos SOS solo aplican a Tiendas. Agrega "Tiendas" en "Aplicable a" para habilitar esta opción.</span>
                                    </p>
                                )}
                            </div>
                        );
                    }}
                </Form.Item>

                <Form.Item noStyle shouldUpdate={(prev, curr) => prev.applicableModels !== curr.applicableModels}>
                    {({ getFieldValue }) => {
                        const models = getFieldValue('applicableModels') || [];
                        const storeIsApplicable = models.includes('Store');

                        if (!hasSosTargets || !storeIsApplicable) return null;

                        return (
                            <Form.List name="sosTargets">
                                {(fields, { add, remove }) => (
                                    <div className="mb-4 space-y-2">
                                        {fields.map(({ key, name, ...restField }) => (
                                            <div key={key} className="flex items-start gap-2 rounded-lg border border-surface-200 p-2.5">
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'category']}
                                                    rules={[{ required: true, message: 'Categoría requerida' }]}
                                                    className="!mb-0 flex-1 min-w-0">
                                                    <Select
                                                        showSearch
                                                        placeholder="Seleccionar categoría"
                                                        optionFilterProp="children"
                                                        filterOption={(input, option) => (option?.children ?? '').toLowerCase().includes(input.toLowerCase())}>
                                                        {categoryList.map((cat) => (
                                                            <Option key={cat.name} value={cat.value}>
                                                                {cat.value}
                                                            </Option>
                                                        ))}
                                                    </Select>
                                                </Form.Item>
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'targetPercentage']}
                                                    rules={[
                                                        { required: true, message: 'Porcentaje requerido' },
                                                        { type: 'number', min: 0, max: 100, message: 'Debe estar entre 0 y 100' }
                                                    ]}
                                                    className="!mb-0 w-[140px] shrink-0">
                                                    <InputNumber placeholder="0" min={0} max={100} step={0.1} precision={2} addonAfter="%" className="w-full" />
                                                </Form.Item>
                                                <Button type="text" danger aria-label="Quitar categoría" icon={<CircleMinus />} onClick={() => remove(name)} />
                                            </div>
                                        ))}
                                        <Button type="dashed" onClick={() => add()} block icon={<Plus />}>
                                            Agregar Categoría
                                        </Button>
                                        <p className="m-0 flex items-start gap-1.5 rounded-lg border border-info-border bg-info-bg p-3 text-xs text-info-fg">
                                            <Lightbulb className="mt-px shrink-0" />
                                            <span>Define el porcentaje objetivo por categoría. Ej: Arroz 40%, Pasta 60%, Harina 25%</span>
                                        </p>
                                    </div>
                                )}
                            </Form.List>
                        );
                    }}
                </Form.Item>

                <div className="flex justify-end gap-2 border-t border-surface-100 pt-4">
                    <Button onClick={onCancel}>Cancelar</Button>
                    <Button type="primary" htmlType="submit" loading={loading}>
                        {tag ? 'Actualizar' : 'Crear'}
                    </Button>
                </div>
            </Form>
        </Modal>
    );
}

export default TagFormModal;
